import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Ban } from 'lucide-react';
import { EASE } from './Reveal';

const R = 41;
const BLOB = 19;
const FLY_MS = 650;
const TOSS_MS = 550;
const BLOB_SHAPES = [
  '58% 42% 55% 45% / 48% 60% 40% 52%',
  '45% 55% 48% 52% / 58% 42% 58% 42%',
  '52% 48% 60% 40% / 45% 55% 45% 55%',
  '60% 40% 45% 55% / 55% 45% 55% 45%',
];

const HANDLE_GAP = Math.PI / 3;

const pos = (i, n) => {
  const a = HANDLE_GAP / 2 + ((i + 0.5) * (Math.PI * 2 - HANDLE_GAP)) / n;
  return { left: 50 + R * Math.cos(a), top: 50 + R * Math.sin(a) };
};

const Sizzle = () => (
  <div className="absolute inset-0 pointer-events-none" data-testid="pan-sizzle">
    {Array.from({ length: 6 }).map((_, k) => (
      <motion.span
        key={k}
        initial={{ opacity: 0, y: 0, scale: 0.6 }}
        animate={{ opacity: [0, 0.7, 0], y: -80 - k * 10, scale: 1.3, x: (k % 2 ? 1 : -1) * (6 + k * 3) }}
        transition={{ duration: 0.9, delay: k * 0.06, ease: 'easeOut' }}
        className="absolute w-5 h-5 rounded-full bg-cream/70 blur-[3px]"
        style={{ left: `${30 + k * 8}%`, top: '30%' }}
      />
    ))}
    {Array.from({ length: 8 }).map((_, k) => (
      <motion.span
        key={`b${k}`}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: [0, 1, 0], scale: [0, 1, 0.4] }}
        transition={{ duration: 0.45, delay: 0.05 + k * 0.06 }}
        className="absolute w-2 h-2 rounded-full bg-butter"
        style={{ left: `${18 + ((k * 37) % 64)}%`, top: `${18 + ((k * 53) % 64)}%` }}
      />
    ))}
  </div>
);

export const ButterPan = ({ works, onOpenCase }) => {
  const [anim, setAnim] = useState(() => {
    const saved = localStorage.getItem('bm-anim');
    if (saved !== null) return saved === '1';
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  const [selected, setSelected] = useState(null);
  const [phase, setPhase] = useState('idle');
  const timers = useRef([]);

  useEffect(() => localStorage.setItem('bm-anim', anim ? '1' : '0'), [anim]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const pick = (w) => {
    if (phase !== 'idle') return;
    if (!anim) { onOpenCase(w); return; }
    setSelected(w);
    setPhase('flying');
    timers.current = [
      setTimeout(() => setPhase('tossing'), FLY_MS),
      setTimeout(() => { onOpenCase(w); setPhase('idle'); setSelected(null); }, FLY_MS + TOSS_MS),
    ];
  };

  return (
    <div data-testid="butter-pan-section" className="relative">
      <div className="flex justify-end mb-4">
        <button
          type="button"
          data-testid="pan-animation-toggle"
          aria-pressed={anim}
          onClick={() => setAnim((a) => !a)}
          className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold px-4 py-2 rounded-full border transition-colors ${
            anim ? 'bg-ink text-cream border-ink' : 'border-ink/30 text-ink/60 hover:border-ink hover:text-ink'
          }`}
        >
          {anim ? <Sparkles size={13} /> : <Ban size={13} />}
          Animation {anim ? 'On' : 'Off'}
        </button>
      </div>

      <div data-testid="butter-pan" className="relative w-full max-w-[720px] aspect-square mx-auto select-none [perspective:1200px]">
        <div data-testid="pan-handle" className="absolute left-[74%] top-1/2 -translate-y-1/2 w-[24%] h-[7%] bg-ink rounded-full shadow-[0_6px_0_#00000033]" />
        <div className="absolute left-[92%] top-1/2 -translate-y-1/2 w-[4%] h-[4%] bg-cream rounded-full" />

        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[54%] h-[54%]">
          <motion.div
            data-testid="pan-body"
            animate={
              phase === 'tossing'
                ? { rotateX: [0, 28, -10, 0], y: [0, 26, -10, 0], scale: [1, 0.96, 1.03, 1] }
                : phase === 'flying'
                  ? { rotateX: 0, y: [0, 0, 6], scale: 1 }
                  : { rotateX: 0, y: 0, scale: 1 }
            }
            transition={{ duration: phase === 'tossing' ? TOSS_MS / 1000 : FLY_MS / 1000, ease: 'easeInOut' }}
            style={{ transformOrigin: '50% 100%' }}
            className="absolute inset-0 rounded-full bg-ink shadow-[0_18px_40px_-12px_rgba(26,26,26,0.55)]"
          >
            <div className="absolute inset-[4%] rounded-full bg-[radial-gradient(circle_at_40%_35%,#3a3a3a,#1A1A1A_70%)] border border-cream/10 overflow-hidden">
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center">
                <img src="/assets/logo-butter.png" alt="Butter Media" className="w-[62%]" />
                <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-cream/55">
                  {phase === 'idle' ? 'Pick a blob to start cooking' : phase === 'flying' ? 'Sizzling…' : 'Serving up'}
                </p>
              </div>
              {phase === 'tossing' && <Sizzle />}
            </div>
            <div className="absolute inset-0 rounded-full ring-[6px] ring-butter/70 ring-inset pointer-events-none" />
          </motion.div>
        </div>

        {works.map((w, i) => {
          const isSel = selected?.id === w.id;
          const p = pos(i, works.length);
          const toCenter = { x: `${((50 - p.left) * 100) / BLOB}%`, y: `${((50 - p.top) * 100) / BLOB}%` };
          return (
            <div key={w.id} className="absolute w-[19%] aspect-square -translate-x-1/2 -translate-y-1/2" style={{ left: `${p.left}%`, top: `${p.top}%` }}>
              <motion.button
                type="button"
                data-testid={`pan-blob-${w.id}`}
                aria-label={`${w.client} — ${w.title}`}
                title={w.client}
                onClick={() => pick(w)}
                initial={false}
                animate={
                  isSel && phase === 'tossing'
                    ? { ...toCenter, scale: 7, opacity: 0, rotate: 0 }
                    : isSel
                      ? { ...toCenter, scale: 2.2, opacity: 1, rotate: 0 }
                      : anim
                        ? { x: 0, scale: 1, opacity: 1, y: [0, -7, 0, 5, 0], rotate: [0, 2.5, 0, -2.5, 0] }
                        : { x: 0, y: 0, scale: 1, opacity: 1, rotate: 0 }
                }
                transition={
                  isSel && phase === 'tossing'
                    ? { duration: TOSS_MS / 1000, ease: [0.3, 0, 0.8, 0.2] }
                    : isSel
                      ? { duration: FLY_MS / 1000, ease: EASE }
                      : anim
                        ? { y: { duration: 4 + (i % 3), repeat: Infinity, ease: 'easeInOut', delay: i * 0.35 }, rotate: { duration: 5 + (i % 2), repeat: Infinity, ease: 'easeInOut', delay: i * 0.2 }, scale: { duration: 0 }, opacity: { duration: 0.2 } }
                        : { duration: 0 }
                }
                whileHover={phase === 'idle' ? { scale: 1.08 } : {}}
                whileTap={phase === 'idle' ? { scale: 0.96 } : {}}
                className={`group absolute inset-0 border-2 border-ink bg-butter overflow-hidden shadow-[4px_5px_0_#1A1A1A] ${isSel ? 'z-30' : 'z-10'} ${phase !== 'idle' && !isSel ? 'pointer-events-none' : ''}`}
                style={{ borderRadius: BLOB_SHAPES[i % BLOB_SHAPES.length] }}
              >
                <img src={w.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                <span className="hidden sm:flex absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/85 to-transparent pb-2 pt-8 justify-center">
                  <span className="block max-w-[62%] text-cream text-[9px] font-bold uppercase tracking-[0.08em] text-center leading-[1.15] break-words">{w.client}</span>
                </span>
              </motion.button>
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-center text-[11px] uppercase tracking-[0.25em] text-ink/50">
        Tap a blob — we’ll plate the case study for you.
      </p>
    </div>
  );
};
